import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ForwardedRef } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../api/client";
import { useCursorInfinite } from "../api/useCursorInfinite";
import type {
  AnalysisEnvelope,
  AnalysisHistoryItem,
  AnalysisHistoryResponse,
  MaterialClusterPageItem,
  MaterialClustersPageResponse,
  TradingCluster,
  TradingFeedResponse,
} from "../api/types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2 } from "@/components/icons";
import { alertEmitter, deriveAlerts } from "./alerts";
import { ArticleDrawer } from "./ArticleDrawer";
import { ALERT_HIGHLIGHT_MS, DEFAULT_HISTORY_LIMIT, REFRESH_COOLDOWN_MS } from "./constants";
import { buildTapeModel } from "./feedModel";
import { FreshWall } from "./FreshWall";
import { PaneHead } from "./PaneHead";
import { StickySignalCard } from "./StickySignalCard";
import { MaterialChart } from "./material-chart/MaterialChart";
import { Tape } from "./Tape";
import type { FocusableTapeItem, PaneSide, SeverityThresholds } from "./types";
import { useNow } from "./useNow";
import { useTradingFeed } from "./useTradingFeed";
import { useNotesCounts } from "@/notes/hooks";
import type { NoteTargetKind } from "@/api/notes";
import type { NoteTarget as NoteTargetType } from "@/notes/NotesUIController";
import { useLanguage } from "@/i18n/useLanguage";

export interface PaneFeedStatus {
  materialId: string;
  state: "loading" | "error" | "ready" | "refreshing";
  asOf: string | null;
}

export interface PaneHandle {
  scrollToItem: (id: string) => void;
  focusPane: () => void;
}

export type PaneKeyboardCommand =
  | { pane: PaneSide; sequence: number; type: "move"; direction: 1 | -1 }
  | { pane: PaneSide; sequence: number; type: "toggle" }
  | { pane: PaneSide; sequence: number; type: "escape" }
  | { pane: PaneSide; sequence: number; type: "refresh" };

// NoteTarget moved to `@/notes/NotesUIController` so the shell-level
// AskTapelens chat can share the same target shape. Re-exported here for
// backward compatibility with existing trading callers.
export type NoteTarget = NoteTargetType;

interface PaneProps {
  side: PaneSide;
  materialId: string;
  otherMaterialId: string;
  focused: boolean;
  fullscreen: boolean;
  searchFocusSignal: number;
  keyboardCommand: PaneKeyboardCommand | null;
  mutedUntilTs: number;
  severityThresholds: SeverityThresholds;
  onMaterialChange: (materialId: string) => void;
  onFocusPane: (side: PaneSide) => void;
  onToggleFullscreen: (side: PaneSide) => void;
  onJumpToOtherPane: (clusterId: string, materialId: string) => void;
  onFeedStatusChange: (side: PaneSide, status: PaneFeedStatus) => void;
  onOpenNotes?: (target: NoteTarget) => void;
  // Slice 3 / Task F1: notify the dashboard controller when the user
  // selects a cluster or expands an analysis snapshot. The controller
  // publishes these into the `dashboard_focus` readable so the chat
  // agent can address the user's actual selection.
  onClusterSelect?: (clusterId: string | null) => void;
  onAnalysisExpand?: (analysisId: string | null) => void;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Unable to load trading feed.";
}

function refreshMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 429) {
    return `Refresh cooling down for ${Math.round(REFRESH_COOLDOWN_MS / 1_000)}s.`;
  }
  return errorMessage(error);
}

/**
 * Map a cursor-paginated /materials/{id}/analysis/history item into the
 * AnalysisEnvelope shape the Tape expects. The two endpoints return nearly
 * the same fields; the cursor endpoint uses `llm_model` where the trading
 * feed uses `model`.
 */
/**
 * Map a cursor-paginated /materials/{id}/clusters item into the TradingCluster
 * shape buildTapeModel expects. The two shapes are nearly identical after
 * the backend enrichment (commit 17c6bab in news-analyzer-api) — this mapper
 * just drops the cursor-only `assigned_at` field that TradingCluster doesn't
 * carry.
 */
function tradingClusterFromPageItem(item: MaterialClusterPageItem): TradingCluster {
  return {
    cluster_id: item.cluster_id,
    representative_title: item.representative_title,
    primary_category: item.primary_category,
    categories: item.categories,
    first_seen_at: item.first_seen_at,
    distinct_wire_origins: item.distinct_wire_origins,
    member_count: item.member_count,
    wire_origins_sample: item.wire_origins_sample,
    enrichment: item.enrichment,
    impact: item.impact,
    also_impacts_materials: item.also_impacts_materials,
  };
}

function envelopeFromHistoryItem(item: AnalysisHistoryItem): AnalysisEnvelope {
  return {
    id: item.id,
    generated_at: item.generated_at,
    validation_status: item.validation_status,
    trigger_kind: item.trigger_kind,
    model: item.llm_model,
    llm_input_tokens: item.llm_input_tokens,
    llm_output_tokens: item.llm_output_tokens,
    llm_cost_usd: item.llm_cost_usd ?? undefined,
    langfuse_trace_id: item.langfuse_trace_id ?? undefined,
    body_hash: item.body_hash,
    body: item.body ?? null,
  };
}

function PaneImpl({
  side,
  materialId,
  otherMaterialId,
  focused,
  fullscreen,
  searchFocusSignal,
  keyboardCommand,
  mutedUntilTs,
  severityThresholds,
  onMaterialChange,
  onFocusPane,
  onToggleFullscreen,
  onJumpToOtherPane,
  onFeedStatusChange,
  onOpenNotes,
  onClusterSelect,
  onAnalysisExpand,
}: PaneProps, ref: ForwardedRef<PaneHandle>) {
  const { t } = useTranslation("dashboard");
  const { language } = useLanguage();
  const queryClient = useQueryClient();
  const paneRef = useRef<HTMLElement | null>(null);
  const mountTimeRef = useRef(Date.now());
  const previousFeedRef = useRef<TradingFeedResponse | null>(null);
  const highlightTimeoutRef = useRef<number | null>(null);
  const lastKeyboardCommandRef = useRef(0);
  const feedQuery = useTradingFeed(materialId, DEFAULT_HISTORY_LIMIT);
  // i18n Slice 2 — a lightweight parallel fetch of the current analysis
  // envelope JUST for `language_served`. The trading-feed endpoint
  // returns the analysis body but predates the i18n envelope, so we hit
  // /materials/{id}/analysis?lang=<user-lang> to learn which language
  // the repository actually served. Used to drive <LanguageBadge> on the
  // sticky signal card. We don't replace the trading-feed-derived
  // currentAnalysis with this — the body is identical and the trading
  // feed bundles related cluster/history data we already need.
  const analysisLangQuery = useQuery({
    queryKey: ["material-analysis-lang", materialId, language],
    queryFn: () => api.getMaterialAnalysis(materialId, language),
    enabled: Boolean(materialId),
    staleTime: 30_000,
  });
  const analysisLanguageServed = analysisLangQuery.data?.language_served ?? null;
  // Cursor-paginated "load older analysis runs" — seeded with the oldest
  // analysis_history item the trading-feed first paint returned. Stays
  // disabled until the trading feed has data so we don't fire a request
  // against an empty cursor and double-count the first page.
  const oldestTradingFeedHistory =
    feedQuery.data?.analysis_history?.[feedQuery.data.analysis_history.length - 1] ?? null;
  const olderHistoryQuery = useCursorInfinite<AnalysisHistoryResponse>({
    queryKey: ["material-analysis-history", materialId, oldestTradingFeedHistory?.id ?? null],
    fetchPage: (cursor) =>
      api.listMaterialAnalysisHistoryPage(materialId, { ...cursor, include_body: true }),
    enabled: Boolean(oldestTradingFeedHistory),
    initialCursor: oldestTradingFeedHistory
      ? { before: oldestTradingFeedHistory.generated_at, before_id: oldestTradingFeedHistory.id }
      : undefined,
    limit: 10,
  });
  const olderHistoryItems = useMemo<AnalysisEnvelope[]>(
    () =>
      olderHistoryQuery.data?.pages.flatMap((p) =>
        p.items.map(envelopeFromHistoryItem),
      ) ?? [],
    [olderHistoryQuery.data],
  );

  // Cursor-paginated "load older clusters" — seeded with the oldest cluster
  // the trading-feed first paint returned. The standalone endpoint now
  // returns the rich TradingCluster shape (backend commit 17c6bab) so the
  // dashboard's ClusterCard can render older entries without degradation.
  const oldestTradingFeedCluster =
    feedQuery.data?.clusters?.[feedQuery.data.clusters.length - 1] ?? null;
  const olderClustersQuery = useCursorInfinite<MaterialClustersPageResponse>({
    queryKey: ["material-clusters-older", materialId, oldestTradingFeedCluster?.cluster_id ?? null],
    fetchPage: (cursor) => api.listMaterialClustersPage(materialId, cursor),
    enabled: Boolean(oldestTradingFeedCluster),
    initialCursor: oldestTradingFeedCluster
      ? {
          before: oldestTradingFeedCluster.first_seen_at,
          before_id: oldestTradingFeedCluster.cluster_id,
        }
      : undefined,
    limit: 50,
  });
  const olderClusters = useMemo<TradingCluster[]>(
    () =>
      olderClustersQuery.data?.pages.flatMap((p) =>
        p.items.map(tradingClusterFromPageItem),
      ) ?? [],
    [olderClustersQuery.data],
  );
  const now = useNow();
  const [message, setMessage] = useState<string | null>(null);
  const [analysisExpanded, setAnalysisExpanded] = useState(false);
  const [expandedSnapshotIds, setExpandedSnapshotIds] = useState<Set<string>>(() => new Set());
  const [expandedClusterIds, setExpandedClusterIds] = useState<Set<string>>(() => new Set());
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [highlightedItemId, setHighlightedItemId] = useState<string | null>(null);
  const [drawerState, setDrawerState] = useState<{
    articleId: string;
    siblings: string[];
    expanded: boolean;
  } | null>(null);
  const tapeModel = useMemo(() => {
    if (!feedQuery.data) return null;
    return buildTapeModel({
      currentAnalysis: feedQuery.data.current_analysis,
      history: [...feedQuery.data.analysis_history, ...olderHistoryItems],
      clusters: [...feedQuery.data.clusters, ...olderClusters],
    });
  }, [feedQuery.data, olderHistoryItems, olderClusters]);
  const focusableItems = useMemo<FocusableTapeItem[]>(() => {
    if (!tapeModel) return [];
    const items: FocusableTapeItem[] = [];
    for (const cluster of tapeModel.freshClusters) {
      items.push({ id: cluster.cluster_id, kind: "cluster" });
    }
    for (const group of tapeModel.groups) {
      items.push({ id: group.snapshot.id, kind: "snapshot" });
      for (const item of group.clusters) {
        items.push({ id: item.cluster.cluster_id, kind: "cluster" });
      }
    }
    return items;
  }, [tapeModel]);

  // Build the full list of note targets visible in this pane so we can
  // batch-fetch counts in a single request and look them up per-card.
  const noteTargets = useMemo<Array<{ kind: NoteTargetKind; id: string }>>(() => {
    if (!tapeModel || !onOpenNotes) return [];
    const targets: Array<{ kind: NoteTargetKind; id: string }> = [];
    for (const cluster of tapeModel.freshClusters) {
      targets.push({ kind: "cluster", id: cluster.cluster_id });
    }
    for (const group of tapeModel.groups) {
      targets.push({ kind: "analysis", id: group.snapshot.id });
      for (const item of group.clusters) {
        targets.push({ kind: "cluster", id: item.cluster.cluster_id });
      }
    }
    return targets;
  }, [tapeModel, onOpenNotes]);

  const notesCountsQuery = useNotesCounts(noteTargets);
  // Build a lookup map keyed as "kind:id" matching the API's serialised format.
  const notesCountsMap = useMemo<Record<string, number>>(() => {
    if (!notesCountsQuery.data) return {};
    return notesCountsQuery.data as Record<string, number>;
  }, [notesCountsQuery.data]);

  const refreshMutation = useMutation({
    mutationFn: () => api.refreshMaterial(materialId),
    onSuccess: (response) => {
      const estimate = response.estimated_completion_seconds
        ? ` Estimated ${response.estimated_completion_seconds}s.`
        : "";
      setMessage(response.scheduled === false ? "Refresh already queued." : `Refresh scheduled.${estimate}`);
      void queryClient.invalidateQueries({ queryKey: ["trading-feed", materialId] });
    },
    onError: (error) => {
      setMessage(refreshMessage(error));
    },
  });

  const currentAnalysis = feedQuery.data?.current_analysis ?? null;
  const state: PaneFeedStatus["state"] = feedQuery.isError
    ? "error"
    : feedQuery.isPending
      ? "loading"
      : feedQuery.isFetching
        ? "refreshing"
        : "ready";

  useEffect(() => {
    onFeedStatusChange(side, {
      materialId,
      state,
      asOf: feedQuery.data?.as_of ?? null,
    });
  }, [feedQuery.data?.as_of, materialId, onFeedStatusChange, side, state]);

  useEffect(() => {
    const data = feedQuery.data;
    if (!data) return;
    const previous = previousFeedRef.current;
    const nowMs = Date.now();
    if (previous && previous.material_id !== data.material_id) {
      previousFeedRef.current = data;
      return;
    }

    const alerts = deriveAlerts({
      pane: side,
      previous,
      next: data,
      now: nowMs,
      highThreshold: severityThresholds.high,
    }).filter((alert) => {
      if (previous) return true;
      if (alert.kind !== "high-cluster") return false;
      const cluster = data.clusters.find((candidate) => candidate.cluster_id === alert.targetId);
      return cluster ? Date.parse(cluster.first_seen_at) > mountTimeRef.current : false;
    });

    if (alerts.length > 0 && mutedUntilTs <= nowMs) {
      alerts.forEach((alert) => alertEmitter.emit(alert));
    }
    previousFeedRef.current = data;
  }, [feedQuery.data, mutedUntilTs, severityThresholds.high, side]);

  useImperativeHandle(
    ref,
    () => ({
      focusPane: () => {
        paneRef.current?.focus();
      },
      scrollToItem,
    }),
    [],
  );

  useEffect(() => {
    if (keyboardCommand == null || keyboardCommand.pane !== side) return;
    if (lastKeyboardCommandRef.current === keyboardCommand.sequence) return;
    lastKeyboardCommandRef.current = keyboardCommand.sequence;

    if (keyboardCommand.type === "move") moveSelection(keyboardCommand.direction);
    else if (keyboardCommand.type === "toggle") toggleSelected();
    else if (keyboardCommand.type === "escape") handleEscape();
    else if (keyboardCommand.type === "refresh") refreshMutation.mutate();
  }, [keyboardCommand, side]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current != null) window.clearTimeout(highlightTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setMessage(null);
    setAnalysisExpanded(false);
    setExpandedSnapshotIds(new Set());
    setExpandedClusterIds(new Set());
    setSelectedItemId(null);
    setHighlightedItemId(null);
    setDrawerState(null);
    previousFeedRef.current = null;
    mountTimeRef.current = Date.now();
    // Reset the controller-level selection signals so the dashboard_focus
    // readable doesn't keep publishing a stale id from the previous
    // material.
    onClusterSelect?.(null);
    onAnalysisExpand?.(null);
  }, [materialId, onClusterSelect, onAnalysisExpand]);

  const scrollToItem = (id: string) => {
    setSelectedItemId(id);
    setHighlightedItemId(id);
    const target = Array.from(
      paneRef.current?.querySelectorAll<HTMLElement>("[data-tape-id]") ?? [],
    ).find((element) => element.dataset.tapeId === id);
    target?.scrollIntoView({ block: "center" });
    if (highlightTimeoutRef.current != null) window.clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = window.setTimeout(() => {
      setHighlightedItemId((current) => (current === id ? null : current));
    }, ALERT_HIGHLIGHT_MS);
  };

  const jumpToCluster = (clusterId: string) => {
    scrollToItem(clusterId);
  };

  const toggleCluster = (clusterId: string) => {
    setSelectedItemId(clusterId);
    setExpandedClusterIds((current) => {
      const next = new Set(current);
      const willExpand = !next.has(clusterId);
      if (next.has(clusterId)) next.delete(clusterId);
      else next.add(clusterId);
      // M3-supp: mirror the toggleSnapshot contract — publish the
      // cluster id when expanding, clear when collapsing. Previously
      // selectedClusterId was published unconditionally, so collapsing
      // the only-expanded cluster left it as "selected" even though no
      // cluster was actually visible in the pane.
      onClusterSelect?.(willExpand ? clusterId : null);
      return next;
    });
  };

  const toggleSnapshot = (snapshotId: string) => {
    setSelectedItemId(snapshotId);
    setExpandedSnapshotIds((current) => {
      const next = new Set(current);
      const willExpand = !next.has(snapshotId);
      if (next.has(snapshotId)) next.delete(snapshotId);
      else next.add(snapshotId);
      // Publish the snapshot id as the "expanded analysis" when toggled
      // open; clear it when collapsed (mirrors the readable's single-value
      // contract — last-expanded analysis on the focused pane).
      onAnalysisExpand?.(willExpand ? snapshotId : null);
      return next;
    });
  };

  const moveSelection = (direction: 1 | -1) => {
    if (focusableItems.length === 0) return;
    const currentIndex = selectedItemId
      ? focusableItems.findIndex((item) => item.id === selectedItemId)
      : -1;
    const nextIndex =
      currentIndex < 0
        ? direction === 1
          ? 0
          : focusableItems.length - 1
        : (currentIndex + direction + focusableItems.length) % focusableItems.length;
    scrollToItem(focusableItems[nextIndex].id);
  };

  const toggleSelected = () => {
    if (!selectedItemId) {
      moveSelection(1);
      return;
    }
    const item = focusableItems.find((focusableItem) => focusableItem.id === selectedItemId);
    if (!item) return;
    if (item.kind === "snapshot") toggleSnapshot(item.id);
    else toggleCluster(item.id);
    scrollToItem(item.id);
  };

  const handleEscape = () => {
    if (drawerState) {
      setDrawerState(null);
      return;
    }
    if (!selectedItemId) return;
    const item = focusableItems.find((focusableItem) => focusableItem.id === selectedItemId);
    if (item?.kind === "snapshot") {
      setExpandedSnapshotIds((current) => {
        const next = new Set(current);
        next.delete(item.id);
        return next;
      });
    } else if (item?.kind === "cluster") {
      setExpandedClusterIds((current) => {
        const next = new Set(current);
        next.delete(item.id);
        return next;
      });
    }
  };

  const loadOlderRuns = () => {
    // Single sentinel drives both pagination sources — older runs (analysis
    // history) and older clusters terminate independently, so we keep
    // calling fetchNextPage on each until that side returns next_before_id
    // = null and hasNextPage becomes false.
    if (olderHistoryQuery.hasNextPage && !olderHistoryQuery.isFetchingNextPage) {
      olderHistoryQuery.fetchNextPage();
    }
    if (olderClustersQuery.hasNextPage && !olderClustersQuery.isFetchingNextPage) {
      olderClustersQuery.fetchNextPage();
    }
  };

  const openArticle = (articleId: string, siblingIds: string[]) => {
    setDrawerState({ articleId, siblings: siblingIds, expanded: false });
  };

  return (
    <section
      aria-label={`${side} trading pane`}
      className={`trading-pane${focused ? " focused" : ""}${fullscreen ? " fullscreen" : ""}`}
      data-pane-side={side}
      onFocus={() => onFocusPane(side)}
      ref={paneRef}
      tabIndex={-1}
    >
      <PaneHead
        focused={focused}
        materialId={materialId}
        onFocusPane={() => onFocusPane(side)}
        onMaterialChange={onMaterialChange}
        onRefresh={() => refreshMutation.mutate()}
        onToggleFullscreen={() => onToggleFullscreen(side)}
        refreshMessage={message}
        refreshPending={refreshMutation.isPending}
        searchFocusSignal={searchFocusSignal}
        side={side}
      />
      <MaterialChart materialId={materialId} />
      <div className="pane-body">
        <div className="pane-scroll">
          <StickySignalCard
            analysis={currentAnalysis}
            materialId={materialId}
            expanded={analysisExpanded}
            nextRunAt={feedQuery.data?.material.next_run_at ?? null}
            now={now}
            notesCount={currentAnalysis ? (notesCountsMap[`analysis:${currentAnalysis.id}`] ?? 0) : 0}
            requestedLanguage={language}
            servedLanguage={analysisLanguageServed}
            onClusterJump={jumpToCluster}
            onToggleExpanded={() => {
              setAnalysisExpanded((value) => {
                const next = !value;
                onAnalysisExpand?.(next && currentAnalysis ? currentAnalysis.id : null);
                return next;
              });
            }}
            onOpenNotes={
              onOpenNotes && currentAnalysis
                ? () => onOpenNotes({ kind: "analysis", id: currentAnalysis.id })
                : undefined
            }
          />
          {feedQuery.isPending ? (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{t("pane.loadingFeed", { material: materialId })}</span>
            </div>
          ) : null}
          {feedQuery.isError ? (
            <div className="px-4 py-3">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{errorMessage(feedQuery.error)}</AlertDescription>
              </Alert>
            </div>
          ) : null}
          {feedQuery.data && tapeModel ? (
            <div className="pane-feed">
              <div className="pane-metrics">
                <div>
                  <strong>{tapeModel.freshClusters.length}</strong>
                  <span>{t("pane.metrics.fresh")}</span>
                </div>
                <div>
                  <strong>{tapeModel.groups.length}</strong>
                  <span>{t("pane.metrics.runs")}</span>
                </div>
                <div>
                  <strong>{feedQuery.data.clusters.length}</strong>
                  <span>{t("pane.metrics.total")}</span>
                </div>
              </div>
              <FreshWall
                clusters={tapeModel.freshClusters}
                expandedClusterIds={expandedClusterIds}
                highlightedItemId={highlightedItemId}
                materialId={materialId}
                muted={mutedUntilTs > Date.now()}
                now={now}
                notesCountsMap={onOpenNotes ? notesCountsMap : undefined}
                onJumpToOtherPane={onJumpToOtherPane}
                onOpenArticle={openArticle}
                onOpenClusterNotes={
                  onOpenNotes
                    ? (clusterId, title) => onOpenNotes({ kind: "cluster", id: clusterId, title })
                    : undefined
                }
                onToggleCluster={toggleCluster}
                otherMaterialId={otherMaterialId}
                selectedItemId={selectedItemId}
                since={currentAnalysis?.generated_at ?? null}
                thresholds={severityThresholds}
              />
              <Tape
                expandedClusterIds={expandedClusterIds}
                expandedSnapshotIds={expandedSnapshotIds}
                groups={tapeModel.groups}
                hasMoreOlder={
                  olderHistoryQuery.hasNextPage !== false ||
                  olderClustersQuery.hasNextPage !== false
                }
                highlightedItemId={highlightedItemId}
                isLoadingOlder={
                  olderHistoryQuery.isFetchingNextPage ||
                  olderClustersQuery.isFetchingNextPage
                }
                materialId={materialId}
                now={now}
                notesCountsMap={onOpenNotes ? notesCountsMap : undefined}
                onJumpToOtherPane={onJumpToOtherPane}
                onLoadOlderRuns={loadOlderRuns}
                onOpenArticle={openArticle}
                onOpenClusterNotes={
                  onOpenNotes
                    ? (clusterId, title) => onOpenNotes({ kind: "cluster", id: clusterId, title })
                    : undefined
                }
                onToggleCluster={toggleCluster}
                onToggleSnapshot={toggleSnapshot}
                otherMaterialId={otherMaterialId}
                selectedItemId={selectedItemId}
                thresholds={severityThresholds}
              />
              {feedQuery.data.clusters.length === 0 ? (
                <div className="pane-state">{t("pane.emptyFeed")}</div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      {drawerState ? (
        <ArticleDrawer
          articleId={drawerState.articleId}
          expanded={drawerState.expanded}
          onClose={() => setDrawerState(null)}
          onSelectArticle={(articleId) =>
            setDrawerState((current) =>
              current ? { ...current, articleId } : current,
            )
          }
          onToggleExpanded={() =>
            setDrawerState((current) =>
              current ? { ...current, expanded: !current.expanded } : current,
            )
          }
          siblingArticleIds={drawerState.siblings}
        />
      ) : null}
    </section>
  );
}

export const Pane = forwardRef(PaneImpl);
